/* eslint-disable simple-import-sort/imports -- prismjs core must load before its language components */
import Prism from 'prismjs';
import 'prismjs/components/prism-markdown';
/* eslint-enable simple-import-sort/imports */

import {clsx} from 'clsx';
import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import {Item} from 'semantic-ui-react';
import type {VFile} from 'vfile';

import {generateHTML} from '../util/markdown';

const formatContent = async (content: string, condensed?: boolean) => {
  if (!content || content.length === 0) {
    return '';
  }
  if (condensed) {
    const parts = content.split(/#+/);
    content = parts[0].length === 0 ? '### ' + parts[1] : parts[0];
    return await generateHTML(content);
  } else {
    return await generateHTML(content);
  }
};

interface MarkdownEditorProps {
  content: string;
  condensed?: boolean;
  onContentHeightChange?(h: number): void;
  shouldEjectFromTailwind?: boolean;
}

const Markdown: React.FC<MarkdownEditorProps> = ({
  content,
  condensed,
  onContentHeightChange,
  shouldEjectFromTailwind = true,
}) => {
  const ref = useRef<HTMLDivElement>(null);
  const [html, setHTML] = useState<string | VFile>(
    '<div class="ui active loader"/>'
  );

  useEffect(() => {
    let cancelled = false;
    formatContent(content, condensed).then(formatted => {
      if (cancelled) {
        return;
      }
      setHTML(formatted);
    });

    return () => {
      cancelled = true;
    };
  }, [content, condensed]);

  useLayoutEffect(() => {
    if (ref.current) {
      const code = ref.current.querySelectorAll('code');
      for (let i = 0; i < code.length; i++) {
        Prism.highlightElement(code[i]);
      }
    }
  }, [html]);

  const lastHeight = useRef<number | null>(null);

  const updateHeight = useCallback(() => {
    const contentHeight = ref.current?.offsetHeight;
    if (contentHeight != null && contentHeight !== lastHeight.current) {
      lastHeight.current = contentHeight;
      onContentHeightChange?.(contentHeight);
    }
  }, [onContentHeightChange]);

  useEffect(() => {
    if (ref.current == null || onContentHeightChange == null) {
      return;
    }
    window.addEventListener('resize', updateHeight);

    // Images load asynchronously and affect the content height
    const imgs = ref.current.querySelectorAll('img');
    // We have to use classic for loop here because IE doesn't support NodeList.forEach()
    // See https://developer.mozilla.org/en-US/docs/Web/API/NodeList#Example
    for (let i = 0; i < imgs.length; i++) {
      const img = imgs[i];
      img.addEventListener('load', updateHeight);
    }

    updateHeight();
    return () => {
      window.removeEventListener('resize', updateHeight);
    };
  });

  useLayoutEffect(() => {
    updateHeight();
  }, [html, updateHeight]);

  // The `tw-eject` class is used to optionally eject from `.tw-style` resets if this component happens to be rendered with a `.tw-style` parent in the tree
  // see: src/wandbTailwindPreflight.css
  return (
    <div
      ref={ref}
      className="markdown-content"
      data-testid="markdown-container">
      <Item.Description
        className={clsx({
          'tw-eject': shouldEjectFromTailwind,
          markdown: condensed,
        })}
        dangerouslySetInnerHTML={{
          __html: html,
        }}
      />
    </div>
  );
};
export default Markdown;
